import { useState, useEffect, useRef, useCallback } from 'react';
import {
    useGetDealerAddressPriceListQuery,
    useGetDealerAddressPriceListByIdQuery,
    useAddDealerAddressPriceListMutation,
    useUpdateDealerAddressPriceListMutation,
    useGetDealerAddressPriceListDropdownQuery
} from '../../../../../api/endpoints/priceList';
import { useGetDealerAddressByDealerIdQuery } from '../../../../../api/endpoints/dealeraddress';

const useCustomerPriceList = () => {
    const toast = useRef(null);
    const isEditDataSet = useRef(false);

    const [dialogVisible, setDialogVisible] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [currentAddressPrice, setCurrentAddressPrice] = useState(null);
    const [editItemId, setEditItemId] = useState(null);
    const [submitted, setSubmitted] = useState(false);

    const [lazyParams, setLazyParams] = useState({ first: 0, page: 1, rows: 10 });
    const [filters, setFilters] = useState({});

    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [selectedAddress, setSelectedAddress] = useState(null);
    const [selectedPriceList, setSelectedPriceList] = useState(null);
    const [usageType, setUsageType] = useState(null);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    const { data: priceListData } = useGetDealerAddressPriceListDropdownQuery();
    const priceListOptions = priceListData || [];

    const {
        data: editItemData,
        isLoading: isLoadingEditItem,
        refetch: refetchEditItem
    } = useGetDealerAddressPriceListByIdQuery(editItemId, {
        skip: !editItemId,
        refetchOnMountOrArgChange: 'always'
    });

    const dealerId = selectedCustomer?.dealerId || selectedCustomer?.id;
    const { data: addressOptions = [] } = useGetDealerAddressByDealerIdQuery(Number(dealerId), {
        skip: !dealerId
    });

    const { data, isLoading, refetch } = useGetDealerAddressPriceListQuery({
        ...filters,
        activeOnly: filters.activeOnly !== undefined ? filters.activeOnly : 1,
        pageNumber: lazyParams.page,
        pageSize: lazyParams.rows
    });

    const [addDealer] = useAddDealerAddressPriceListMutation();
    const [updateDealer] = useUpdateDealerAddressPriceListMutation();

    useEffect(() => {
        setSelectedAddress(null);
    }, [dealerId]);

    // 🆕 Price list seçimini her dialog açıldığında set et
    useEffect(() => {
        if (editMode && dialogVisible && priceListOptions && editItemData?.data) {
            const price = priceListOptions.find((p) => p.listHeaderId === editItemData.data.listHeaderId);
            setSelectedPriceList(price || null);
        }
    }, [editMode, dialogVisible, editItemData?.data, priceListData]);

    // 🆕 Form verilerini her dialog açıldığında set et
    useEffect(() => {
        if (editMode && dialogVisible && editItemData?.data) {
            setUsageType(editItemData.data.usageType);
            setStartDate(editItemData.data.contractStartDate ? new Date(editItemData.data.contractStartDate) : null);
            setEndDate(editItemData.data.contractEndDate ? new Date(editItemData.data.contractEndDate) : null);
        }
    }, [editMode, dialogVisible, editItemData?.data]);

    const handleApplyFilters = useCallback((updatedFilters) => {
        const newFilters = {};
        Object.entries(updatedFilters).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== '') {
                if (key.toLowerCase().includes('date') && value instanceof Date) {
                    newFilters[key] = value.toISOString();
                } else {
                    newFilters[key] = value;
                }
            }
        });
        setFilters(newFilters);
    }, []);

    const handleResetFilters = () => {
        setFilters({});
    };

    const onPage = useCallback((event) => {
        setLazyParams({ page: event.page + 1, rows: event.rows });
    }, []);

    const resetForm = () => {
        setSelectedCustomer(null);
        setSelectedAddress(null);
        setSelectedPriceList(null);
        setUsageType(null);
        setStartDate(null);
        setEndDate(null);
        isEditDataSet.current = false;
    };

    const handleAddNew = () => {
        setEditMode(false);
        setEditItemId(null);
        setCurrentAddressPrice(null);
        resetForm();
        setSubmitted(false);
        setDialogVisible(true);
    };

    const handleEdit = (addressPrice) => {
        setEditMode(true);
        setEditItemId(addressPrice.id);
        setCurrentAddressPrice(addressPrice);
        resetForm();
        setSubmitted(false);
        setDialogVisible(true);
    };

    const handleSubmit = async () => {
        setSubmitted(true);

        const dealerIdInt = Number(selectedCustomer?.dealerId || selectedCustomer?.id || selectedCustomer);
        const addressId = selectedAddress?.addrId || selectedAddress?.id || selectedAddress;
        const priceListId = selectedPriceList?.listHeaderId || selectedPriceList?.id || selectedPriceList;

        if (dealerIdInt && addressId && priceListId && startDate && usageType !== null) {
            const payload = {
                dealerId: dealerIdInt,
                dealerAddressId: addressId,
                listHeaderId: priceListId,
                usageType,
                contractStartDate: startDate.toISOString(),
                contractEndDate: endDate ? endDate.toISOString() : null,
                createdBy: 'UI',
                createdSession: 'WEB'
            };
            try {
                if (editMode && currentAddressPrice) {
                    await updateDealer({ id: currentAddressPrice.id, ...payload });
                    toast.current?.show({
                        severity: 'success',
                        summary: 'Güncellendi',
                        detail: 'Fiyat listesi güncellendi',
                        life: 3000
                    });
                } else {
                    await addDealer(payload);
                    toast.current?.show({
                        severity: 'success',
                        summary: 'Eklendi',
                        detail: 'Fiyat listesi eklendi',
                        life: 3000
                    });
                }

                setDialogVisible(false);
                refetch();
            } catch (error) {
                console.error('API Hatası:', error);
                toast.current?.show({
                    severity: 'error',
                    summary: 'Hata',
                    detail: 'Kayıt başarısız',
                    life: 3000
                });
            }
        }
    };

    return {
        toast,
        dialogVisible,
        setDialogVisible,
        handleAddNew,
        resetForm,
        handleApplyFilters,
        handleResetFilters,
        tableProps: {
            data: data?.items || [],
            totalRecords: data?.totalCount || 0,
            isLoading,
            onPage,
            lazyParams: { first: (lazyParams.page - 1) * lazyParams.rows, rows: lazyParams.rows },
            onEdit: handleEdit,
            handleAddNew,
            resetPagination: false
        },
        formProps: {
            submitted,
            editMode,
            selectedCustomer,
            setSelectedCustomer,
            selectedAddress,
            setSelectedAddress,
            selectedPriceList,
            setSelectedPriceList,
            usageType,
            setUsageType,
            startDate,
            setStartDate,
            endDate,
            setEndDate,
            addressOptions,
            priceListOptions,
            handleSubmit,
            currentAddressPrice,
            toast,
            refetch,
            isLoadingEditItem
        }
    };
};

export default useCustomerPriceList;
